import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { merge, of as observableOf } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { ApiService } from '../../services/api/api.service';
import { TranslocoModule } from '@ngneat/transloco';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { VulnerabilityAssessment } from '../../interfaces/VulnerabilityAssessment';
import { VulnerabilityAssessmentResponse } from '../../interfaces/VulnerabilityAssessmentResponse';
import { Company } from '../../interfaces/Company';
import { CompanyResponse } from '../../interfaces/CompanyResponse';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../common/confirm-dialog/confirn-dialog.component';
import { AlertDialogComponent } from '../../common/alert-dialog/alert-dialog.component';
import { GlobalConstants } from '../../common/global-constants';
import * as XLSX from 'xlsx';

@Component({
    selector: 'app-v-assessments',
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatTooltipModule,
        TranslocoModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './v-assessments.component.html',
    styleUrl: './v-assessments.component.scss',
})
export class VAssessmentsComponent {
    displayedColumns: string[] = [
        'assessmentName',
        'description',
        'companyName',
        'branchOfficeName',
        'startDate',
        'vulnerabilitiesCount',
        'action',
    ];
    data: VulnerabilityAssessment[] = [];
    dataSource = new MatTableDataSource<VulnerabilityAssessment>(this.data);
    totalItems = 0;
    pageSizes = [10, 20, 50, 1000];
    isLoadingResults = true;
    isRateLimitReached = false;
    companies: Company[] = [];

    readonly dialog = inject(MatDialog);

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;
    @ViewChild('TABLE') table: ElementRef;

    // isToggled
    isToggled = false;

    internal = GlobalConstants.internal;

    constructor(
        public themeService: CustomizerSettingsService,
        private apiService: ApiService
    ) {
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit() {
        if (this.roleName == GlobalConstants.internal) {
            this.apiService
                .getCompanyPagination(0, 1000000, 'name', 'asc')
                .subscribe((response: CompanyResponse) => {
                    this.companies.push(...response.companies);
                    // If the user changes the sort order, reset back to the first page.
                    this.sort.sortChange.subscribe(
                        () => (this.paginator.pageIndex = 0)
                    );

                    merge(this.sort.sortChange, this.paginator.page)
                        .pipe(
                            startWith({}),
                            switchMap(() => {
                                this.isLoadingResults = true;
                                return this.apiService
                                    .getVulnerabilityAssessment(
                                        this.paginator.pageIndex,
                                        this.paginator.pageSize,
                                        this.sort.active,
                                        this.sort.direction
                                    )
                                    .pipe(catchError(() => observableOf(null)));
                            }),
                            map((data) => {
                                // Flip flag to show that loading has finished.
                                this.isLoadingResults = false;
                                //this.isRateLimitReached = data === null;

                                if (data === null) {
                                    return [];
                                } else {
                                    console.log(data.vulnerabilityAssessments);
                                    data.vulnerabilityAssessments.forEach(
                                        (elem) => {
                                            const result = this.companies.find(
                                                ({ id }) =>
                                                    id ===
                                                    elem.branchOffice.companyId
                                            );
                                            if (result) {
                                                elem.branchOffice.companyName =
                                                    result.name;
                                            }
                                        }
                                    );
                                }

                                // Only refresh the result length if there is new data. In case of rate
                                // limit errors, we do not want to reset the paginator to zero, as that
                                // would prevent users from re-triggering requests.
                                this.totalItems = data.totalElements;
                                return data.vulnerabilityAssessments;
                            })
                        )
                        .subscribe((data) => (this.dataSource.data = data));
                });
        } else {
            this.apiService
                .getCompanyByCompanyUser()
                .subscribe((response: CompanyResponse) => {
                    this.companies.push(...[response.company]);
                    // If the user changes the sort order, reset back to the first page.
                    this.sort.sortChange.subscribe(
                        () => (this.paginator.pageIndex = 0)
                    );

                    merge(this.sort.sortChange, this.paginator.page)
                        .pipe(
                            startWith({}),
                            switchMap(() => {
                                this.isLoadingResults = true;
                                switch (this.roleName) {
                                    case GlobalConstants.manager:
                                        return this.apiService
                                            .getVulnerabilityAssessmentByCompany(
                                                this.paginator.pageIndex,
                                                this.paginator.pageSize,
                                                this.sort.active,
                                                this.sort.direction
                                            )
                                            .pipe(
                                                catchError(() =>
                                                    observableOf(null)
                                                )
                                            );
                                    case GlobalConstants.supervisor:
                                        return this.apiService
                                            .getVulnerabilityAssessmentByBranchOffice(
                                                this.paginator.pageIndex,
                                                this.paginator.pageSize,
                                                this.sort.active,
                                                this.sort.direction
                                            )
                                            .pipe(
                                                catchError(() =>
                                                    observableOf(null)
                                                )
                                            );
                                    case GlobalConstants.specialist:
                                        return this.apiService
                                            .getVulnerabilityAssessmentByCompanyUser(
                                                this.paginator.pageIndex,
                                                this.paginator.pageSize,
                                                this.sort.active,
                                                this.sort.direction
                                            )
                                            .pipe(
                                                catchError(() =>
                                                    observableOf(null)
                                                )
                                            );
                                    default:
                                        return this.apiService
                                            .getVulnerabilityAssessment(
                                                this.paginator.pageIndex,
                                                this.paginator.pageSize,
                                                this.sort.active,
                                                this.sort.direction
                                            )
                                            .pipe(
                                                catchError(() =>
                                                    observableOf(null)
                                                )
                                            );
                                }
                            }),
                            map((data) => {
                                // Flip flag to show that loading has finished.
                                this.isLoadingResults = false;
                                //this.isRateLimitReached = data === null;

                                if (data === null) {
                                    return [];
                                }

                                // Only refresh the result length if there is new data. In case of rate
                                // limit errors, we do not want to reset the paginator to zero, as that
                                // would prevent users from re-triggering requests.
                                this.totalItems = data.totalElements;
                                return data.vulnerabilityAssessments;
                            })
                        )
                        .subscribe((data) => (this.dataSource.data = data));
                });
            this.dataSource.filterPredicate = function (
                data,
                filter: string
            ): boolean {
                return (
                    data.assessmentName.toLowerCase().includes(filter) ||
                    (data.description ? data.description : '')
                        .toLowerCase()
                        .includes(filter) ||
                    (data.branchOffice.company.name
                        ? data.branchOffice.company.name
                        : ''
                    )
                        .toLowerCase()
                        .includes(filter) ||
                    (data.branchOffice.name ? data.branchOffice.name : '')
                        .toLowerCase()
                        .includes(filter)
                );
            };
        }
    }

    /**
     * Getter for role name
     */
    get roleName(): string {
        return localStorage.getItem('roleName') ?? '';
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // Search Filter
    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    // onKeydown
    onKeydown(event: Event) {
        //console.log(event);
        return false;
    }

    // Delete
    delete(id: string) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                action: 'delete',
                component: 'assessment',
                content: 'content-delete',
                buttonText: {
                    ok: 'yes',
                    cancel: 'no',
                },
            },
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (confirmed) {
                console.log('Delete Vulnerability Assessment: ' + id);
                this.apiService.deleteVulnerabilityAssessment(id).subscribe({
                    next: (value: VulnerabilityAssessmentResponse) => {
                        console.log(value);
                        this.ngOnInit();
                    },
                    error: (error) => {
                        let errMsg: string = error;
                        const dialogRef = this.dialog.open(
                            AlertDialogComponent,
                            {
                                data: {
                                    title: 'ERROR',
                                    message: errMsg,
                                },
                            }
                        );
                    },
                });
            }
        });
    }

    exportTOExcel() {
        const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(
            this.table.nativeElement
        );
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Assessments');

        /* save to file */
        XLSX.writeFile(wb, 'assessments.xlsx');
    }
}
